// client/src/pages/instructors/InstructorDashboard.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import { GlobalContext } from '../../context/provider';
import useInstructor from '../../hooks/useInstructor';
import useCourse from '../../hooks/useCourse';
import useAuditLog from '../../hooks/useAuditLog';
import useMessage from '../../hooks/useMessage';
import useAnnouncement from '../../hooks/useAnnouncement';
import MessageAnnouncementNotificationManager, { 
  updateMessageCount, 
  updateAnnouncementCount,
  clearMessageNotifications,
  clearAnnouncementNotifications
} from '../../utilities/messaging/messageAnnouncementNotificationManager';

import '../../styles/globalstyles.css';
import './instructorDashboard.css';

const InstructorDashboard = () => {
  const navigate = useNavigate();
  const { 
    authState: { authData },
  } = useContext(GlobalContext);
  
  const { 
    instructorData, 
    getDashboard,
    getPendingLabs,
    getPendingQuizzes,
    getPendingExams,
    getPendingCapstones 
  } = useInstructor();

  const { 
    courseData,
    getAllCourses,
    getPublishedCourses,
  } = useCourse();

  const { 
    getMessagesAndMarkSeen,
    sendMessage,
    getUnreadCount,
    getUsersWithUnreadCounts,
    messages,
    unreadCount,
    usersWithUnreadCounts
  } = useMessage();

  const { 
    getAnnouncementHistory,
    getUnreadAnnouncementCount
  } = useAnnouncement();

  const { getUserActivity } = useAuditLog();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [pendingGrading, setPendingGrading] = useState({
    labs: [],
    quizzes: [],
    exams: [],
    capstones: []
  });
  const [gradedGrading, setGradedGrading] = useState({
    labs: [],
    quizzes: [],
    exams: [],
    capstones: []
  });
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalCourses: 0,
    pendingSubmissions: 0,
    gradedSubmissions: 0,
    // avgCourseCompletion: 0
  });
  
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [error, setError] = useState(null);

  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [dashboardDetail, setDashboardDetail] = useState({});

  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [announcementHistory, setAnnouncementHistory] = useState([]);
  const [newAnnouncementsCount, setNewAnnouncementsCount] = useState(0);
  const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(0);

  useEffect(() => {
    loadDashboardData();
    getAllCourses();
  }, [authData.id]); // Only run when instructor changes

  // Track notifications when unread count or announcements change
  useEffect(() => {
    if (!authData?.id) return;
    
    // Simply use the unread counts directly - no need for notification manager
    setNewMessagesCount(unreadCount || 0);
    setNewAnnouncementsCount(unreadAnnouncementCount || 0);
    
    console.log("📊 Updated notification counts:", {
      messages: unreadCount || 0,
      announcements: unreadAnnouncementCount || 0,
      total: (unreadCount || 0) + (unreadAnnouncementCount || 0)
    });
    
  }, [unreadCount, unreadAnnouncementCount, authData?.id]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // ✅ ONE call gets everything you need
      const dashboardResponse = await getDashboard(authData.id);
      const dashboardData = dashboardResponse.data;
      
      console.log('Dashboard data loaded:', dashboardData);
      
      // ✅ Set assigned courses
      setAssignedCourses(dashboardData?.courses || []);
      
      // ✅ Set dashboard stats from summary
      setDashboardStats({
        totalStudents: dashboardData?.summary?.totalStudents,
        totalCourses: dashboardData?.summary?.assignedCourses,
        pendingSubmissions: 
          dashboardData?.summary?.pendingGrading?.labs +
          dashboardData?.summary?.pendingGrading?.quizzes +
          dashboardData?.summary?.pendingGrading?.exams +
          dashboardData?.summary?.pendingGrading?.capstones,
        gradedSubmissions: dashboardData?.summary?.gradingStats?.totalGraded || 0,
        // avgCourseCompletion: 68 // Calculate this if needed
      });

      setDashboardDetail({
        courses: dashboardData?.courses,
        gradedLabs: dashboardData?.gradedLabs,
        gradedQuizzes: dashboardData?.gradedQuizzes,
        gradedExams: dashboardData?.gradedExams,
        gradedCapstones: dashboardData?.gradedCapstones,
      });
      
      // ✅ If you need detailed pending items, make separate calls
      // Only call these if you're displaying the detailed lists
      // if (needDetailedPendingLists) {
      const [pendingLabs, pendingQuizzes, pendingExams, pendingCapstones] = 
        await Promise.all([
          getPendingLabs(authData.id, null),
          getPendingQuizzes(authData.id, null),
          getPendingExams(authData.id, null),
          getPendingCapstones(authData.id, null)
        ]);
      
      setPendingGrading({
        labs: pendingLabs?.data || [],
        quizzes: pendingQuizzes?.data || [],
        exams: pendingExams?.data || [],
        capstones: pendingCapstones?.data || []
      });
      
      // setGradedGrading({
      //   labs: dashboardData.summary.gradingStats?.labsGraded || 0,
      //   quizzes: dashboardData.summary.gradingStats?.quizzesGraded || 0,
      //   exams: dashboardData.summary.gradingStats?.examsGraded || 0,
      //   capstones: dashboardData.summary.gradingStats?.capstonesGraded || 0,
      // });
      setGradedGrading({
        labs: dashboardData?.summary?.gradingStats?.labsGraded || 0,
        quizzes: dashboardData?.summary?.gradingStats?.quizzesGraded || 0,
        exams: dashboardData?.summary?.gradingStats?.examsGraded || 0,
        capstones: dashboardData?.summary?.gradingStats?.capstonesGraded || 0,
      });

      // ✅ Load recent activities (if needed)
      await loadRecentActivities();
      
// ✅ Load recent activities (if needed)
      await loadRecentActivities();
      
      // ✅ ADD THIS NEW BLOCK:
      // Load unread messages count
      try {
        await getUnreadCount(authData.id);
      } catch (error) {
        console.error('Failed to load unread count:', error);
      }

      // Load unread announcement count
      try {
        const unreadAnnouncementResponse = await getUnreadAnnouncementCount(authData.id);
        if (unreadAnnouncementResponse.success) {
          setUnreadAnnouncementCount(unreadAnnouncementResponse.unreadCount || 0);
          console.log("📢 Unread Announcements Count:", unreadAnnouncementResponse.unreadCount);
        }
      } catch (error) {
        console.error('Failed to load unread announcement count:', error);
      }
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
      showNotification('Failed to load dashboard data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    setActivitiesLoading(true);
    try {
      const response = await getUserActivity(authData.id, { limit: 10 });

      if (response.success) {
        const formattedActivities = formatActivities(response.data);
        setRecentActivities(formattedActivities);
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatActivities = (activities) => {
    return activities.map(activity => {
      const icon = getActivityIcon(activity.action);
      const description = getDescription(activity);
      const timeAgo = getTimeAgo(new Date(activity.timestamp));

      return {
        icon,
        title: getActivityTitle(activity.action),
        description,
        timeAgo,
        timestamp: activity.timestamp
      };
    });
  };

  const getActivityIcon = (action) => {
    const iconMap = {
      'CREATE_INSTRUCTOR': '➕',
      'UPDATE_INSTRUCTOR': '📝',
      'ADD_LESSON': '🚀',
      'ADD_LAB': '🚀',
      'ADD_QUIZ': '🚀',
      'ADD_EXAM': '🚀',
      'ADD_CAPSTONE': '🚀',
      'REMOVE_LESSON': '🗑️',
      'REMOVE_LAB': '🗑️',
      'REMOVE_QUIZ': '🗑️',
      'REMOVE_EXAM': '🗑️',
      'REMOVE_CAPSTONE': '🗑️',
      'GRADE_LAB': '🔢',
      'GRADE_QUIZ': '🔢',
      'GRADE_EXAM': '🔢',
      'GRADE_CAPSTONE': '🏅',
    };
    return iconMap[action] || '📋';
  };

  const getActivityTitle = (action) => {
    const titleMap = {
      'CREATE_INSTRUCTOR': "Instructor Profile Created",
      'UPDATE_INSTRUCTOR': "Instructor Profile Updated",
      'ADD_LESSON': "Lesson Added",
      'ADD_LAB': "Lab Added",
      'ADD_QUIZ': "Quiz Added",
      'ADD_EXAM': "Exam Added",
      'ADD_CAPSTONE': "Capstone Added",
      'REMOVE_LESSON': "Lesson Deleted",
      'REMOVE_LAB': "Lab Deleted",
      'REMOVE_QUIZ': "Quiz Deleted",
      'REMOVE_EXAM': "Exam Deleted",
      'REMOVE_CAPSTONE': "Capstone Deleted",
      'GRADE_LAB': "Lab Graded",
      'GRADE_QUIZ': "Quiz Graded",
      'GRADE_EXAM': "Exam Graded",
      'GRADE_CAPSTONE': "Capstone Graded",
    };
    return titleMap[action] || 'Activity';
  };

  const getDescription = (activity) => {
    if (activity.actionDescription) {
      return activity.actionDescription;
    }
    
    const metadata = activity.metadata || {};
    const entityId = activity.entityId || 'Unknown';
    const entityType = activity.entityType || '';
    
    // Build description from changes array
    if (activity.changes && activity.changes.length > 0) {
      const change = activity.changes[0];
      
      switch(activity.action) {
        case 'GRADE_LAB':
          return `${change.newValue} for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}${metadata.labId ? ` - ${metadata.labId}` : ''}`;
        
        case 'GRADE_QUIZ':
          return `${change.newValue} for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}${metadata.quizId ? ` - ${metadata.quizId}` : ''}`;
        
        case 'GRADE_EXAM':
          return `${change.newValue} for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}${metadata.examId ? ` - ${metadata.examId}` : ''}`;
        
        case 'GRADE_CAPSTONE':
          return `${change.newValue} for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}`;
        
        case 'CREATE_INSTRUCTOR':
          return `Created instructor profile for ${entityId}`;
        
        case 'UPDATE_INSTRUCTOR':
          return `Updated profile: ${activity.changes.map(c => c.field).join(', ')}`;
        
        case 'ADD_LESSON':
          return `Added lesson to ${entityType} ${metadata.courseId || entityId}`;
        
        case 'ADD_LAB':
          return `Added lab to ${entityType} ${metadata.courseId || entityId}`;
        
        case 'ADD_QUIZ':
          return `Added quiz to ${entityType} ${metadata.courseId || entityId}`;
        
        case 'ADD_EXAM':
          return `Added exam to ${entityType} ${metadata.courseId || entityId}`;
        
        case 'ADD_CAPSTONE':
          return `Added capstone to ${entityType} ${metadata.courseId || entityId}`;
        
        case 'REMOVE_LESSON':
          return `Removed lesson from ${entityType} ${metadata.courseId || entityId}`;
        
        case 'REMOVE_LAB':
          return `Removed lab from ${entityType} ${metadata.courseId || entityId}`;
        
        case 'REMOVE_QUIZ':
          return `Removed quiz from ${entityType} ${metadata.courseId || entityId}`;
        
        case 'REMOVE_EXAM':
          return `Removed exam from ${entityType} ${metadata.courseId || entityId}`;
        
        case 'REMOVE_CAPSTONE':
          return `Removed capstone from ${entityType} ${metadata.courseId || entityId}`;
        
        case 'LOGIN':
          return `Logged in as ${entityType} ${entityId}`;
        
        case 'SUBMIT':
          return change.newValue || `Uploaded to ${entityType} ${entityId}`;
        
        case 'READ':
          return change.newValue || `Downloaded from ${entityType} ${entityId}`;
        
        case 'DELETE':
          return `Deleted from ${entityType} ${entityId}`;
        
        default:
          // Generic: show change details
          return `${change.field}: ${change.newValue || change.oldValue} (${entityType} ${entityId})`;
      }
    }
    
    // Final fallback
    return `${activity.action} on ${entityType} ${entityId}`;
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    
    return 'just now';
  };

  const handleViewCourse = (course) => {
    navigate(`/instructor/course/${course.courseId}`);
  };

  const handleGradeSubmissions = (type) => {
    showNotification(`Grading interface for ${type} coming soon`, 'info');
  };

  const handleViewCourseDetails = (courseId) => {
    showNotification('Course details page coming soon', 'info');
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = 'notification-toast';
    
    const bgColors = {
      success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      info: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    };
    
    const icons = {
      success: '✅',
      error: '❌',
      info: 'ℹ️'
    };
    
    notification.style.background = bgColors[type] || bgColors.info;
    notification.innerHTML = `
      <span class="notification-icon">${icons[type] || icons.info}</span>
      <span>${message}</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out forwards';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const profileHeaderData = {
    ...authData,
    title: `👨‍🏫 Instructor Dashboard`,
    subTitle: `Welcome, ${instructorData?.name || authData?.name}`
  };

  return (
    <div className="main-container">
      <div className="main-content">
        
        {/* Header */}
        <div className="header-row">
          <ProfileHeader 
            data={profileHeaderData} 
            activePage="instructor-dashboard" 
          />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader 
            notificationCount={newMessagesCount + newAnnouncementsCount} 
            icons={['envelope']} 
          />
        </div>

        {/* Page Title */}
        {/* <div className="page-title">
          <h2>👨‍🏫 Instructor Dashboard</h2>
          <p>Welcome, {instructorData?.title} {instructorData?.name || authData?.name}</p>
        </div> */}

        {/* Tab Navigation */}
        <div className="top-tab-container">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>

          <button
            className={`tab-button ${activeTab === 'courses' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('courses')}
          >
            📚 My Courses ({dashboardStats.totalCourses})
            {/* ({assignedCourses.length}) */}
          </button>

          {/* <button
            className={`tab-button ${activeTab === 'analytics' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            📈 Analytics
          </button> */}
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading dashboard...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="scrollable-list" style={{maxHeight: '70vh'}}>

                {/* Error Display */}
                {error && (
                  <div className="search-container">
                    <div style={{ 
                      padding: '15px', 
                      background: '#fee', 
                      color: '#c33', 
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      ⚠️ {error}
                    </div>
                  </div>
                )}

                {/* Statistics Cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">👨‍🎓</div>
                    <div className="stat-info">
                      <h3>{dashboardStats.totalStudents}</h3>
                      <p>Total Students</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-info">
                      <h3>{dashboardStats.pendingSubmissions}</h3>
                      <p>Pending Grading</p>
                      <small style={{ color: '#666', fontSize: '12px', marginTop: '5px' }}>
                        Labs: {pendingGrading.labs.length} | 
                        Quizzes: {pendingGrading.quizzes.length} | 
                        Exams: {pendingGrading.exams.length} | 
                        Capstones: {pendingGrading.capstones.length}
                      </small>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                      <h3>{dashboardStats.gradedSubmissions}</h3>
                      <p>Graded Submissions</p>
                      <small style={{ color: '#666', fontSize: '12px', marginTop: '5px' }}>
                        Labs: {gradedGrading.labs} |
                        Quizzes: {gradedGrading.quizzes} |
                        Exams: {gradedGrading.exams} |
                        Capstones: {gradedGrading.capstones} |
                      </small>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="recent-activity">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>📋 Recent Platform Activity</h3>
                    <button 
                      onClick={loadRecentActivities}
                      style={{
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>
                  
                  {activitiesLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="loader"></div>
                      <p>Loading activities...</p>
                    </div>
                  ) : recentActivities.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                      <p>No recent activities found</p>
                    </div>
                  ) : (
                    <div className="activity-list">
                      {recentActivities.map((activity, index) => (
                        <div key={index} className="activity-item">
                          <span className="activity-icon">{activity.icon}</span>
                          <div className="activity-details">
                            <p><strong>{activity.title}:</strong> {activity.description}</p>
                            <span className="activity-time">{activity.timeAgo}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="recent-activity">
                <h3>📚 My Courses - Quick Access</h3>
                {assignedCourses.length > 0 ? (
                  <div className="activity-list">
                    {assignedCourses.slice(0, 5).map((course) => {
                      // const progress = calculateProgress(course);
                      // const grade = courseGrades[course.courseId];
                      
                      return (
                        <div key={course.courseId} className="activity-item">
                          <span className="activity-icon">📚</span>
                          <div className="activity-details" style={{ flex: 1 }}>
                            <p><strong>{course.title}</strong></p>
                            <p className="expertise">
                              {course.category || 'General'} •
                              {` ${course.level}` || 'All Levels'} • 
                              {course.credits ? ` ${course.credits} Credits` : ' Self-paced'}
                            </p>
                          </div>

                          <CustomButton 
                            buttonTitle="Continue"
                            action={() => handleViewCourse(course)}
                            bgColor="#667eea"
                          />
                        </div>
                      );
                    })}
                    {assignedCourses.length > 5 && (
                      <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <CustomButton 
                          buttonTitle="View All Courses"
                          action={() => setActiveTab('my-courses')}
                          bgColor="#3b82f6"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-items">
                    <p>No courses enrolled yet 📚</p>
                    <CustomButton 
                      buttonTitle="Explore Courses"
                      action={() => setActiveTab('explore')}
                      bgColor="#667eea"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {/* {activeTab === 'analytics' && (
              <div className="instructor-analytics">
                <div className="page-title">
                  <h3>Course Performance Analytics</h3>
                </div>
                <div className="no-items">
                  <p>Analytics dashboard coming soon 📊</p>
                  <p className="hint">Track student performance, completion rates, and more</p>
                </div>
              </div>
            )} */}
          </>
        )}

        {/* Course Detail Modal */}
        {modalVisible && selectedCourse && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-info-group">
                  <div className="modal-header-title">
                    <h3>{selectedCourse.title}</h3>
                  </div>
                </div>
                <div className="modal-actions-group">
                  <button className="modal-close-btn" onClick={() => setModalVisible(false)}>
                    ✖
                  </button>
                </div>
              </div>

              <div className="modal-body">

                {/* Quick Actions */}
                <div className="instructor-quick-actions">
                  <h3>⚡ Quick Actions</h3>
                  <div className="quick-actions-grid">
                    <CustomButton 
                      buttonTitle={`📝 Grade Labs (${pendingGrading.labs.length})`}
                      action={() => handleGradeSubmissions('labs')}
                      bgColor="#667eea"
                    />
                    <CustomButton 
                      buttonTitle={`📋 Grade Quizzes (${pendingGrading.quizzes.length})`}
                      action={() => handleGradeSubmissions('quizzes')}
                      bgColor="#10b981"
                    />
                    <CustomButton 
                      buttonTitle={`📄 Grade Exams (${pendingGrading.exams.length})`}
                      action={() => handleGradeSubmissions('exams')}
                      bgColor="#f59e0b"
                    />
                    <CustomButton 
                      buttonTitle={`🎓 Grade Capstones (${pendingGrading.capstones.length})`}
                      action={() => handleGradeSubmissions('capstones')}
                      bgColor="#8b5cf6"
                    />
                  </div>
                </div>

                <div className="message-stats">
                  <div className="stat-item">
                    <span className="stat-label">👨‍🎓 Students</span>
                    <span className="stat-value">{Math.floor(Math.random() * 30) + 10}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">📊 Completion</span>
                    <span className="stat-value">{Math.floor(Math.random() * 40) + 60}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">⭐ Rating</span>
                    <span className="stat-value">{(Math.random() * 1 + 4).toFixed(1)}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <CustomButton 
                    buttonTitle="📋 View Details"
                    action={() => handleViewCourseDetails(selectedCourse.courseId)}
                    bgColor="#667eea"
                  />
                  <CustomButton 
                    buttonTitle="👥 View Students"
                    action={() => showNotification('Student list coming soon', 'info')}
                    bgColor="#10b981"
                  />
                </div>

                <div className="session-info">
                  <div className="summary-item">
                    <strong>📋 Course ID:</strong> {selectedCourse.courseId}
                  </div>
                  <div className="summary-item">
                    <strong>👤 Your Role:</strong> Primary Instructor
                  </div>
                  <div className="summary-item">
                    <strong>📅 Semester:</strong> Current
                  </div>
                  <div className="summary-item">
                    <strong>📝 Description:</strong> {selectedCourse.description}
                  </div>
                  {selectedCourse.prerequisites && selectedCourse.prerequisites.length > 0 && (
                    <div className="summary-item">
                      <strong>📌 Prerequisites:</strong> {selectedCourse.prerequisites.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default InstructorDashboard;